import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CommonInput, Loader, RegularButton } from 'pg-ui-library';
import { IBenefitLimitedPrice, IBenefitType } from '../../../../interfaces';
import { useBenefitLimitedPrices } from '../../queries/benefit-limited-prices.query';
import { usePatchBenefitLimitedPrices } from '../../mutations/patch-benefit-limited-prices.mutation';
import { Actions, Container, EmptyMessage, ErrorText, InputsContainer, Row, RowsWrapper, Title } from './styles';

const DEFAULT_LIMITED_TYPE = 'default';

type LimitedRowState = {
  id?: string;
  benefitId: string;
  type: string;
  minLimit: string;
  maxLimit: string;
};

export const BenefitLimitedRange: React.FC<{ selectedBenefitType: IBenefitType | undefined }> = ({
  selectedBenefitType,
}) => {
  const { t } = useTranslation();
  const versionId = selectedBenefitType?.versionId || '';

  const limitedBenefits = useMemo(
    () => selectedBenefitType?.typeBenefit.filter(b => b.costCalculationMethod === 'Limited') || [],
    [selectedBenefitType]
  );

  const {
    data: limitedPricesData = [] as IBenefitLimitedPrice[],
    isLoading,
    isFetching,
  } = useBenefitLimitedPrices({ versionId });
  const patchLimitedMutation = usePatchBenefitLimitedPrices();

  const [formState, setFormState] = useState<LimitedRowState[]>([]);

  useEffect(() => {
    const prices = Array.isArray(limitedPricesData) ? limitedPricesData : [];
    const rows = limitedBenefits.map(benefit => {
      const existing = prices.find(item => item.benefitId === benefit.benefitId);
      const normalizedType = existing?.type?.trim() || selectedBenefitType?.type || DEFAULT_LIMITED_TYPE;
      return {
        id: existing?.id,
        benefitId: benefit.benefitId,
        type: normalizedType,
        minLimit: existing?.minLimit !== null && existing?.minLimit !== undefined ? String(existing.minLimit) : '',
        maxLimit: existing?.maxLimit !== null && existing?.maxLimit !== undefined ? String(existing.maxLimit) : '',
      };
    });
    setFormState(rows);
  }, [limitedBenefits, limitedPricesData]);

  const handleInputChange = (benefitId: string, field: 'minLimit' | 'maxLimit', value: string) => {
    setFormState(prev => prev.map(row => (row.benefitId === benefitId ? { ...row, [field]: value } : row)));
  };

  const payload = useMemo(
    () =>
      formState.map(row => ({
        id: row.id,
        versionId,
        benefitId: row.benefitId,
        type: row.type,
        minLimit: row.minLimit === '' ? null : Number(row.minLimit),
        maxLimit: row.maxLimit === '' ? null : Number(row.maxLimit),
      })),
    [formState, versionId]
  );

  const rowValidation = useMemo(
    () =>
      payload.reduce<Record<string, boolean>>((acc, row) => {
        const hasError = row.minLimit !== null && row.maxLimit !== null ? row.minLimit > row.maxLimit : false;
        if (hasError) {
          acc[row.benefitId] = true;
        }
        return acc;
      }, {}),
    [payload]
  );

  const hasErrors = Object.keys(rowValidation).length > 0;

  const handleSave = () => {
    if (!versionId || formState.length === 0) return;
    patchLimitedMutation.mutate(payload);
  };

  const isBusy = isLoading || isFetching;

  let content: React.ReactNode;
  if (isBusy) {
    content = <Loader />;
  } else if (limitedBenefits.length === 0) {
    content = (
      <EmptyMessage>
        {t('benefits.edit.limitedRanges.empty' as any, { defaultValue: 'No limited benefits to configure.' })}
      </EmptyMessage>
    );
  } else {
    content = (
      <>
        <RowsWrapper>
          {formState.map(row => {
            const hasRowError = rowValidation[row.benefitId] || false;
            return (
              <Row key={row.benefitId}>
                <strong>{row.benefitId}</strong>
                <InputsContainer>
                  <CommonInput
                    type="number"
                    label={t('benefits.edit.limitedRanges.minLabel' as any, { defaultValue: 'Minimum limit' })}
                    value={row.minLimit}
                    onChange={e => handleInputChange(row.benefitId, 'minLimit', e.target.value)}
                  />
                  <CommonInput
                    type="number"
                    label={t('benefits.edit.limitedRanges.maxLabel' as any, { defaultValue: 'Maximum limit' })}
                    value={row.maxLimit}
                    onChange={e => handleInputChange(row.benefitId, 'maxLimit', e.target.value)}
                  />
                </InputsContainer>
                {hasRowError && (
                  <ErrorText>
                    {t('benefits.edit.limitedRanges.validation' as any, {
                      defaultValue: 'Maximum limit must be greater than minimum limit.',
                    })}
                  </ErrorText>
                )}
              </Row>
            );
          })}
        </RowsWrapper>
        <Actions>
          <RegularButton
            onClick={handleSave}
            disabled={hasErrors || patchLimitedMutation.isLoading || formState.length === 0}
          >
            {patchLimitedMutation.isLoading ? 'Saving...' : t('global.form.save')}
          </RegularButton>
        </Actions>
      </>
    );
  }

  return (
    <Container>
      <Title>{t('benefits.edit.limitedRanges.title' as any, { defaultValue: 'Limited Benefit Ranges' })}</Title>
      {content}
    </Container>
  );
};
